import { requireSupabase } from "@/lib/supabaseClient";
import { guard } from "@/lib/errors";

const AGENCY_SELECT = "*, owner:profiles!agencies_created_by_fkey(id, full_name, username, avatar_url)";

export const agencyService = {
  getBySlug: (slug) => guard("agency.getBySlug", async () => {
    if (!slug) return null;
    const s = requireSupabase();
    const { data, error } = await s.from("agencies").select("*").ilike("slug", slug).maybeSingle();
    if (error) throw error; return data;
  }),

  list: ({ limit = 60 } = {}) => guard("agency.list", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("agencies").select("*").order("created_at", { ascending: false }).limit(limit);
    if (error) throw error; return data || [];
  }),

  mine: (userId) => guard("agency.mine", async () => {
    if (!userId) return null;
    const s = requireSupabase();
    const { data, error } = await s.from("agencies").select("*").eq("created_by", userId)
      .order("created_at", { ascending: false }).limit(1).maybeSingle();
    if (error) throw error; return data;
  }),

  create: (payload) => guard("agency.create", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("agencies").insert(payload).select().maybeSingle();
    if (error) throw error; return data;
  }),

  update: (id, patch) => guard("agency.update", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("agencies").update(patch).eq("id", id).select().maybeSingle();
    if (error) throw error; return data;
  }),

  // ---- Phase 1B: Team management ----
  members: (agencyId) => guard("agency.members", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("agency_members")
      .select("role_in_agency, status, created_at, profile:profiles!agency_members_profile_id_fkey(id, full_name, username, avatar_url, role)")
      .eq("agency_id", agencyId).order("created_at", { ascending: true });
    if (error) throw error; return data || [];
  }),

  invitations: (agencyId) => guard("agency.invitations", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("agency_invitations")
      .select("*").eq("agency_id", agencyId).eq("status", "pending").order("created_at", { ascending: false });
    if (error) throw error; return data || [];
  }),

  invite: (agencyId, email, invited_role, invitedBy) => guard("agency.invite", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("agency_invitations")
      .insert({ agency_id: agencyId, email: email.trim().toLowerCase(), invited_role, invited_by: invitedBy })
      .select().maybeSingle();
    if (error) throw error; return data;
  }),

  revokeInvite: (id) => guard("agency.revokeInvite", async () => {
    const s = requireSupabase();
    const { error } = await s.from("agency_invitations").update({ status: "revoked" }).eq("id", id);
    if (error) throw error; return true;
  }),

  // Invitations addressed to the current user's email.
  myInvitations: (email) => guard("agency.myInvitations", async () => {
    if (!email) return [];
    const s = requireSupabase();
    const { data, error } = await s.from("agency_invitations")
      .select("*, agency:agencies(id, name, slug, logo_url)")
      .ilike("email", email).eq("status", "pending").order("created_at", { ascending: false });
    if (error) throw error; return data || [];
  }),

  acceptInvite: (invite, userId) => guard("agency.acceptInvite", async () => {
    const s = requireSupabase();
    await s.from("agency_members").upsert(
      { agency_id: invite.agency_id, profile_id: userId, role_in_agency: invite.invited_role, status: "active", invited_by: invite.invited_by, updated_at: new Date().toISOString() },
      { onConflict: "agency_id,profile_id" }
    );
    await s.from("agency_invitations").update({ status: "accepted", accepted_by: userId }).eq("id", invite.id);
    await s.from("profiles").update({ agency_id: invite.agency_id }).eq("id", userId);
    return true;
  }),

  declineInvite: (id) => guard("agency.declineInvite", async () => {
    const s = requireSupabase();
    const { error } = await s.from("agency_invitations").update({ status: "declined" }).eq("id", id);
    if (error) throw error; return true;
  }),

  updateMemberRole: (agencyId, profileId, role_in_agency) => guard("agency.updateMemberRole", async () => {
    const s = requireSupabase();
    const { error } = await s.from("agency_members")
      .update({ role_in_agency, updated_at: new Date().toISOString() })
      .eq("agency_id", agencyId).eq("profile_id", profileId);
    if (error) throw error; return true;
  }),

  removeMember: (agencyId, profileId) => guard("agency.removeMember", async () => {
    const s = requireSupabase();
    const { error } = await s.from("agency_members").delete().eq("agency_id", agencyId).eq("profile_id", profileId);
    if (error) throw error;
    await s.from("profiles").update({ agency_id: null }).eq("id", profileId).eq("agency_id", agencyId);
    return true;
  }),
};

export const developerService = {
  getBySlug: (slug) => guard("developer.getBySlug", async () => {
    if (!slug) return null;
    const s = requireSupabase();
    const { data, error } = await s.from("developers")
      .select("*, owner:profiles(id, full_name, username, avatar_url)")
      .ilike("slug", slug).maybeSingle();
    if (error) throw error; return data;
  }),

  getById: (id) => guard("developer.getById", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("developers").select("*").eq("id", id).maybeSingle();
    if (error) throw error; return data;
  }),

  save: (id, patch) => guard("developer.save", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("developers").upsert({ id, ...patch }, { onConflict: "id" }).select().maybeSingle();
    if (error) throw error; return data;
  }),

  list: ({ limit = 60 } = {}) => guard("developer.list", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("developers").select("*").order("created_at", { ascending: false }).limit(limit);
    if (error) throw error; return data || [];
  }),
};
